import { ChevronLeft, ChevronRight } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import type { PreviewCover, PreviewStructurePages } from '../../types';

/**
 * ═══ STRUCTURE-PAGE MIRROR CONTRACT (Sebastian ruling 2026-09-16) ══════
 *
 * This component reconstructs the structure pages the print composer
 * writes, so the customer sees on the Freigabe screen what the printed
 * book will carry. It is a mirror of the print provider at
 * `src/book/print-pdf/pdf-lib-vl21.print-provider.ts`.
 *
 * Any change to what the printer WRITES on a structure page — the
 * following named functions or the back-panel composition inside the
 * cover render — MUST be mirrored here in the same commit:
 *
 *   - drawTitlePage
 *   - drawDedicationBlankPage
 *   - drawUeberDreamable
 *   - manufacturerImprintFor
 *   - drawBackPanelMarkingBlock (marking block on the back cover)
 *
 * The three text-heavy pages (page 23 Über Dreamable, page 24
 * Impressum, back-cover marking block) are safe by construction: the
 * printer's constants (`UEBER_DREAMABLE_PLACEHOLDER`,
 * `MANUFACTURER_IMPRINT`, `MANUFACTURER_BACK_MARKING`) are re-exported
 * to the preview envelope as-is; a body edit flows automatically. Any
 * other change — new heading literal, new PrintInput-derived field,
 * new drawn element — is a two-file change or the two surfaces drift.
 *
 * A drift-guard test at
 * `src/book/print-pdf/structure-page-content-parity.spec.ts` fires
 * the printer's structure-page draw functions against a fixture and
 * compares the string set they write against the string set the
 * preview envelope exposes for the same fixture. If the two sets
 * diverge, the test goes red. That test is the enforcement — this
 * comment is the human-readable pointer.
 *
 * Line-numbered references to the print provider were deliberately
 * DROPPED from this file's JSDoc on 2026-09-16 — every function it
 * referenced had already moved after commit b3fe75a, and stale line
 * numbers had actively misled a reader into thinking the mirror was
 * up to date. File-name-only, function-name-only pointers only.
 *
 * ═══ WHY A SIBLING, NOT A VARIANT OF `StageView` ═══════════════════════
 *
 * `StageView` is a pure `<img>` wrapper. These slots render typeset
 * TEXT (except the title-page anchor image and the back-cover logo,
 * which are also visual elements). Threading a text-composition
 * branch through StageView would resurrect the dormant overlay layer
 * the 2026-08-23 ruling deleted. This component reuses the geometry
 * (square container, chevron arrows, aria wrapper) but owns its own
 * rendering.
 *
 * ═══ TYPOGRAPHIC FIDELITY ═════════════════════════════════════════════
 *
 * Sebastian: "exact typographic fidelity is not the goal; 'this is
 * what will be printed' is." Content + order + which fields are
 * present are the invariants — position, font, per-mm math are not.
 */

type StructureSlotKind =
  | {
      kind: 'title-page';
      title: string;
      anchorImageUrl: string | null;
      castMembers: PreviewCover['castMembers'];
      creationDateGerman: string;
    }
  | { kind: 'about-book' }
  | { kind: 'about-dreamable'; paragraphs: PreviewStructurePages['ueberDreamable'] }
  | { kind: 'imprint'; lines: PreviewStructurePages['impressum'] }
  | { kind: 'back-cover'; backMarking: PreviewStructurePages['backMarking'] };

export interface StructurePageViewProps {
  readonly slot: StructureSlotKind;
  readonly ariaLabel: string;
  /** True when there is a previous slot in the deck. */
  readonly canGoPrev: boolean;
  /** True when there is a next slot in the deck. */
  readonly canGoNext: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
}

export function StructurePageView({
  slot,
  ariaLabel,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
}: StructurePageViewProps) {
  // Back cover renders on the printed book's navy back panel — white
  // typography on dream-navy. Every other structure slot renders on
  // dream-white with dream-navy text (mirrors the PDF's interior
  // content-page background). The container is square to match the
  // 21×21 cm cut size.
  const isBackCover = slot.kind === 'back-cover';

  return (
    <div
      className={
        (isBackCover ? 'bg-dream-navy text-dream-white ' : 'bg-dream-white text-dream-navy ') +
        'aspect-square w-full rounded-xl shadow-xl overflow-hidden relative'
      }
      aria-label={ariaLabel}
      role="group"
    >
      <StructurePageBody slot={slot} />

      {canGoPrev && (
        <button
          type="button"
          onClick={onPrev}
          aria-label={STRINGS.book.preview.navPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-dream-white/90 flex items-center justify-center text-dream-navy shadow-sm transition-colors hover:bg-dream-white focus-visible:outline-2 focus-visible:outline-dream-navy/50"
        >
          <ChevronLeft aria-hidden className="h-5 w-5" />
        </button>
      )}

      {canGoNext && (
        <button
          type="button"
          onClick={onNext}
          aria-label={STRINGS.book.preview.navNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-dream-white/90 flex items-center justify-center text-dream-navy shadow-sm transition-colors hover:bg-dream-white focus-visible:outline-2 focus-visible:outline-dream-navy/50"
        >
          <ChevronRight aria-hidden className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function StructurePageBody({ slot }: { readonly slot: StructureSlotKind }) {
  switch (slot.kind) {
    case 'title-page':
      return (
        <TitlePageBody
          title={slot.title}
          anchorImageUrl={slot.anchorImageUrl}
          castMembers={slot.castMembers}
          creationDateGerman={slot.creationDateGerman}
        />
      );
    case 'about-book':
      return <DedicationBlankBody />;
    case 'about-dreamable':
      return <AboutDreamableBody paragraphs={slot.paragraphs} />;
    case 'imprint':
      return <ImprintBody lines={slot.lines} />;
    case 'back-cover':
      return <BackCoverBody backMarking={slot.backMarking} />;
  }
}

/**
 * Title page — mirrors `drawTitlePage` on the print side (Sebastian
 * ruling 2026-09-16). Four elements in vertical order top-to-bottom:
 *
 *   1. "Die Crew" heading (bold, top slot at the position the typeset
 *      title used to sit).
 *   2. Cover image — SAME image the front cover shows
 *      (`envelope.cover.imageUrl`). Post-payment: the cover-anchor
 *      render with the painted cartouche (title painted inside).
 *      Pre-slice-1 / pre-payment: book_anchor fallback per the
 *      preview-read cascade — page 1 shows no visible title in that
 *      case, matching the printer's fallback.
 *   3. Cast list (" · "-joined, wraps).
 *   4. Date bottom-right ("Monat Jahr").
 *
 * The typeset book title is deliberately NOT rendered on page 1 —
 * it now lives painted inside the cover image. Empty cast omits the
 * cast block; missing cover imageUrl omits the image slot.
 *
 * The `title` prop is retained on the caller's slot shape for future
 * use (e.g. `<img alt>`) but not rendered as typeset content here.
 */
function TitlePageBody({
  title,
  anchorImageUrl,
  castMembers,
  creationDateGerman,
}: {
  readonly title: string;
  readonly anchorImageUrl: string | null;
  readonly castMembers: PreviewCover['castMembers'];
  readonly creationDateGerman: string;
}) {
  const castLine = castMembers.map((c) => c.name).join(' · ');
  const monthYear = extractMonthYearFromGermanDate(creationDateGerman);
  return (
    <div className="absolute inset-0 flex flex-col items-center px-8 pt-[10%] pb-8">
      <p className="text-lg font-bold text-dream-navy">{STRINGS.book.review.crewHeading}</p>
      {anchorImageUrl !== null && (
        <img
          src={anchorImageUrl}
          alt={title}
          className="mt-6 aspect-square w-[48%] object-cover"
          draggable={false}
        />
      )}
      {castLine !== '' && (
        <p className="mt-6 text-center text-base text-dream-navy break-words">{castLine}</p>
      )}
      {monthYear !== '' && (
        <p className="absolute bottom-6 right-8 text-sm text-dream-navy">{monthYear}</p>
      )}
    </div>
  );
}

/**
 * Split "17. August 2026" → "August 2026" — mirrors
 * `extractMonthYearFromGermanDate` on the print side. Both sides
 * drop the leading "D. " token so page 1 shows month + year only.
 * Returns '' on unparseable input (empty aboutBook.creationDateGerman
 * pre-Position-5 orders) so the slot renders without a date.
 */
function extractMonthYearFromGermanDate(dateGerman: string): string {
  if (dateGerman === '') return '';
  const parts = dateGerman.split(' ');
  if (parts.length >= 3) return parts.slice(1).join(' ');
  return dateGerman;
}

/**
 * Page 22 — dedication blank. Sebastian ruling 2026-09-14 (revised)
 * moved this page from the auto-composed "Über dieses Buch" three-
 * liner to a blank canvas carrying only the released headline. This
 * component follows the print side (`drawDedicationBlankPage`) at
 * 2026-09-16 — pre-fix the PWA was still showing the deleted date
 * sentence.
 *
 * The old `AboutBookBody` shape read `envelope.aboutBook` for
 * creationDateGerman + childName + stadt + childAgeYears. Post-
 * revision none of those fields flow to this page. `envelope.aboutBook`
 * remains on the envelope (its `creationDateGerman` is now used by
 * `TitlePageBody` above via a separate path); the drop of the three
 * secondary fields (childName, childAgeYears, stadt) from this
 * component's consumption is a follow-up cleanup, not this commit's
 * scope.
 */
function DedicationBlankBody() {
  return (
    <div className="absolute inset-0 flex flex-col items-center pt-[15%] px-8">
      <p className="text-xl font-semibold text-center text-dream-navy">
        {STRINGS.book.review.dedicationBlankHeadline}
      </p>
    </div>
  );
}

/**
 * "Über Dreamable" — heading left-aligned near the top, body
 * paragraphs stacked below. Mirrors `drawUeberDreamable` on the
 * print side. Heading uses the envelope-agnostic strings entry;
 * body paragraphs come STRICTLY from `envelope.structurePages.
 * ueberDreamable`. No local placeholder.
 */
function AboutDreamableBody({
  paragraphs,
}: {
  readonly paragraphs: PreviewStructurePages['ueberDreamable'];
}) {
  return (
    <div className="absolute inset-0 flex flex-col gap-4 px-8 pt-10">
      <h2 className="text-lg font-bold text-dream-navy">
        {STRINGS.book.review.aboutDreamableLabel}
      </h2>
      <div className="flex flex-col gap-3">
        {paragraphs.map((para, i) => (
          <p key={i} className="text-sm text-dream-navy leading-relaxed">
            {para}
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * Impressum — bottom-anchored small text block. Mirrors
 * `drawManufacturerImprint` on the print side (block anchored to the
 * inner bottom margin, each line stacked upward on the composer; the
 * DOM equivalent is a bottom-anchored flex column). Rendered at ~10 px
 * (Tailwind `text-xxs`) mirroring the PDF's 8 pt.
 */
function ImprintBody({ lines }: { readonly lines: PreviewStructurePages['impressum'] }) {
  return (
    <div className="absolute inset-0 flex flex-col justify-end px-8 pb-8">
      <div className="flex flex-col gap-1">
        {lines.map((line, i) => (
          <p key={i} className="text-xxs text-dream-navy leading-none">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * Back cover — mirrors the printed back panel. Sebastian ruling
 * 2026-09-16: (a) centred Dreamable logo, (b) marking block at the
 * foot (address + CE mark + age line). Reads the three marking lines
 * from `envelope.structurePages.backMarking` — same source the
 * printer consumes via `MANUFACTURER_BACK_MARKING`.
 *
 * CE mark renders as a text placeholder (bordered box with "CE") —
 * typographic fidelity is not the goal; "there is a CE mark on the
 * printed book" is what the customer needs to see.
 */
function BackCoverBody({ backMarking }: { readonly backMarking: PreviewStructurePages['backMarking'] }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-between px-8 pt-[35%] pb-6">
      <img src="/logo-dreamable.svg" alt="Dreamable" className="w-[50%] max-w-[240px]" />
      <div className="w-full flex flex-col gap-2 text-left">
        {backMarking[0] !== undefined && (
          <p className="text-xs text-dream-white leading-tight">{backMarking[0]}</p>
        )}
        {backMarking[1] !== undefined && (
          <p className="text-xs text-dream-white leading-tight">{backMarking[1]}</p>
        )}
        <div className="mt-1 flex items-center gap-3">
          <span
            aria-label="CE-Kennzeichnung"
            className="flex h-6 min-w-[36px] items-center justify-center rounded-sm border border-dream-white/80 px-1 text-[10px] font-bold text-dream-white"
          >
            CE
          </span>
          {backMarking[2] !== undefined && (
            <p className="text-xs text-dream-white leading-tight">{backMarking[2]}</p>
          )}
        </div>
      </div>
    </div>
  );
}
